<template>
    <div class="flex flex-col rounded bg-white p-5 @container min-h-[588px]" :class="isDemo && 'pointer-events-none'">
            <div class="flex flex-row flex-wrap lg:flex-nowrap justify-between mb-3">
                <p class="text-body-3 text-default-1 font-medium">News</p>
                <router-link :to="{name: 'news'}" class="flex flex-row items-center cursor-pointer" v-if="news.length > 0">
                    <p class="text-body-6 font-medium text-primary-1 mr-1.5">View all</p>
                    <ArrowRight :color="color.hex"/>
                </router-link>
            </div>
            <div v-if="loading" class="overflow-y-scroll no-scrollbar border-box px-1 flex flex-row flex-grow mt-6">
                <div class="w-1/2 skeleton-loader h-[292px] mt-1"/>
                <div class="flex flex-col w-1/2 ml-2">
                    <div class="w-full h-[70px] skeleton-loader mt-1" v-for="i in 4" :key="i"/>
                </div>
            </div>
            <div v-else class="flex flex-col flex-grow">
                <div
                    v-if="news.length === 0"
                    class="flex flex-col items-center md:flex-grow justify-center text-center"
                >
                    <IconBlog :color="color.hex" class="mb-6"/>
                    <p class="text-body-5 text-default-1">
                        Here you will see the news to which you subscribe
                    </p>
                </div>
                <div v-else>
                    <div
                        class="flex flex-row justify-between flex-wrap @xxs:flex-col xxs:gap-4 mlg:gap-0 @lg:flex-row @lg:flex-nowrap">
                        <FeaturedArticleCard
                            :article="news[0]"
                            @toggle-like="toggleLike(news[0].uid)"
                            class="flex-col hover:bg-background-3 p-2 hover:cursor-pointer rounded"
                            :class="isMobile ? 'w-full flex' : '@xxs:hidden @lg:flex @lg:w-[20%] @xxl:w-[20%]'"
                        />
                        <div class="flex flex-col @xs:w-full @lg:w-[80%] @xxl:w-[80%]">
                            <div
                                v-for="(article, index) in news.slice(1)"
                                :key="article.uid"
                                class="flex flex-col hover:bg-background-3 hover:cursor-pointer"
                            >
                                <FeaturedArticleCard
                                    v-if="isMobile"
                                    :article="article"
                                    class="p-2"
                                    @toggle-like="toggleLike(article.uid)"
                                />
                                <ArticleCard
                                    v-else
                                    class="py-5 px-2 box-border"
                                    :article="article"
                                    @toggle-like="toggleLike(article.uid)"
                                />
                                <hr v-if="!isLastElement(index)" class="h-px bg-background-3"
                                    :class="{'mt-4 mb-4' : isMobile}"/>
                            </div>
                        </div>
                    </div>
                </div>

        </div>
        <CoreButton v-if="isAdmin || isContentAdmin || isRepresentative" buttonType="primary" label="Create News" class="w-full"
                    @click="router.push('/news/create')"/>
    </div>
</template>
<script setup>
import CoreButton from "../core/Button.vue";
import IconBlog from "../icons/Blog.vue";
import FeaturedArticleCard from "@/components/custom/news/FeaturedArticleCard.vue";
import ArticleCard from "@/components/custom/news/ArticleCard.vue";
import {useAuth} from "@/stores/AuthStore";
import {storeToRefs} from "pinia";
import {useTheme} from "@/stores/ThemeStore";
import router from "@/router";
import {useIsMobile} from "@/composables/useIsMobile";
import ArrowRight from "@/components/icons/navigation/ArrowRight.vue";
import {computed, onMounted, ref} from "vue";
import {useNews} from "@/composables/useNews";
import {useWidgetDemoData} from "@/composables/useWidgetDemoData";

const {newsDemoData: demoData} = useWidgetDemoData();

const {getArticles, unlikeArticle, likeArticle} = useNews();

const {isMobile} = useIsMobile();

const emit = defineEmits(['toggleLike']);

const props = defineProps({
    isDemo: {
        type: Boolean,
        default: false,
    },
});

const {color} = useTheme();
const authStore = useAuth();
const {isAdmin, isContentAdmin, isRepresentative} = storeToRefs(authStore);

const {organization} = useAuth();

const loading = ref(false);

const data = ref([]);

const news = computed(() => {
    return !props.isDemo ? data.value : demoData;
})

onMounted(async () => {
    if (props.isDemo) {
        return;
    }

    loading.value = true;

    await getArticles(organization.slug, 1, 6, "", "", "", "published").then(
        (response) => {
            data.value = response.data;
        }
    );

    loading.value = false;
})

function toggleLike(articleUid) {
    if (props.isDemo) {
        return;
    }

    const article = news.value.find((article) => article.uid === articleUid);

    if (article.is_liked) {
        unlikeArticle(organization.slug, article.uid)
            .then(() => {
                article.is_liked = false;
                article.total_likes--;
            })
            .catch((error) => {
                console.error(error);
            })
    } else {
        likeArticle(organization.slug, article.uid)
            .then(() => {
                article.is_liked = true;
                article.total_likes++;
            })
            .catch((error) => {
                console.error(error);
            })
    }
}

function isLastElement(index) {
    return index === news.value.length - 2; // to match for the removal of the first element and the fact that the index is 0 based
}
</script>
